import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import uuidv4 from 'uuid/v4';
import { ChatMessageHistoryDataAction } from '../../../Actions/ChatHistory';
import { MessageAction } from '../../../Actions/MessageActions';
import { RecentChatUpdateAction } from '../../../Actions/RecentChatActions';
import {
    ArrowBackward,
    ArrowForward,
    CloseWhite,
    DeleteStoryIcon,
    IconDotsVertical,
    Playbtn3,
    Recordpause,
    SendMessage,
    StoriesLike,
    StoriesLiked,
    StoriesReplyClose,
    ViewIcon,
} from '../../../assets/images';
import { formatUserIdToJid, getUserDetails } from '../../../Helpers/Chat/User';
import IndexedDb from '../../../Helpers/IndexedDb';
import { getMessageObjSender, getRecentChatMsgObj, getThumbBase64URL } from '../../../Helpers/Utility';
import { mergeStoryViewers, removeStoryById, setStories, updateStoryViewerLike } from '../../../slice/storiesSlice';
import { buildStoryGroup } from '../../callbacks';
import SDK from '../../SDK';
import ImageComponent from '../../WebChat/Common/ImageComponent';
import ContentEditable from '../../WebChat/Common/ContentEditable';
import Spinner from '../../WebChat/Common/Spinner/index';
import StoryViewersModal from '../StoryViewersModal';
import logger from '../../../Helpers/logger';

const IMAGE_STORY_DURATION_MS = 5000;
const STORY_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

const StoryMediaPreview = ({ handleMediaClose, selectedMessageData, isMyStory }) => {
    const dispatch = useDispatch();
    const vCardData = useSelector(state => state.vCardData);
    // storyGroups shape: [{ userId, stories: [{ story_id, media_url, story_type, ... }] }, ...]
    const remoteUserStories = useSelector(state => state.stories.stories);
    const locaUserStories = useSelector(state => state.stories.myStories);
    const privacySettingsData = useSelector(state => state.privacySettingsData);

    const storyGroups = isMyStory ? locaUserStories : remoteUserStories;

    // Resolve initial indices once on mount.
    // For the selected user: if hasUnseen is true, start from the first story where seen === 0;
    // otherwise (all seen) start from index 0.
    const { initialUserIndex, initialStoryIndex } = useMemo(() => {
        const activeUserId = selectedMessageData?.userId;
        let userIdx = 0;
        if (activeUserId) {
            const found = storyGroups.findIndex(g => g.userId === activeUserId);
            userIdx = found >= 0 ? found : 0;
        }

        let storyIdx = 0;
        const group = storyGroups[userIdx];
        if (group?.hasUnseen) {
            const firstUnseen = group.stories?.findIndex(s => s.seen === 0);
            storyIdx = firstUnseen >= 0 ? firstUnseen : 0;
        }

        return { initialUserIndex: userIdx, initialStoryIndex: storyIdx };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const [userIndex, setUserIndex] = useState(initialUserIndex);
    const [storyIndex, setStoryIndex] = useState(initialStoryIndex);
    const [currentMediaUrl, setCurrentMediaUrl] = useState('');
    const [loading, setLoading] = useState(true);
    const [isPlaying, setIsPlaying] = useState(true);
    const [progress, setProgress] = useState(0);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [showViewers, setShowViewers] = useState(false);
    const [viewersFetching, setViewersFetching] = useState(false);
    const [viewersPagination, setViewersPagination] = useState({ currentPage: 0, totalPages: 1 });

    const [isLiked, setIsLiked] = useState(false);
    const [showReplyInput, setShowReplyInput] = useState(false);
    const [replyHtml, setReplyHtml] = useState('');

    const localDb = useMemo(() => new IndexedDb(), []);
    const videoRef = useRef(null);
    const bgVideoRef = useRef(null);
    const objectUrlRef = useRef(null);
    const pausedProgressRef = useRef(0); // stores progress% when an image story is paused
    const menuRef = useRef(null);
    const replyRef = useRef(null);
    // Always-fresh ref so fetchMedia never reads stale remoteUserStories via closure
    const remoteUserStoriesRef = useRef(remoteUserStories);
    // Tracks the storyId that is currently rendered — used to detect IQ-driven deletions
    const lastFetchedStoryIdRef = useRef(null);
    useEffect(() => {
        remoteUserStoriesRef.current = remoteUserStories;
    }, [remoteUserStories]);

    // Derived from current indices — always in sync with redux
    const currentUserGroup = storyGroups[userIndex] || { userId: '', stories: [] };
    const currentStories = currentUserGroup.stories || [];
    const currentStory = currentStories[storyIndex] || null;

    const storyId = currentStory?.storyId || currentStory?.story_id;
    // SDK pushes camelCase keys (storyId, mediaUrl, storyType) from the websocket listener
    const fileToken = currentStory?.mediaUrl || currentStory?.media_url;
    const isVideo = (currentStory?.storyType || currentStory?.story_type) === 'video';

    const isAtVeryStart = userIndex === 0 && storyIndex === 0;
    const isAtVeryEnd = userIndex === storyGroups.length - 1 && storyIndex === currentStories.length - 1;

    // ---------- Close dots-menu on outside click ----------

    useEffect(() => {
        if (!isMenuOpen) return;
        const handleOutsideClick = e => {
            if (menuRef.current && !menuRef.current.contains(e.target)) {
                setIsMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', handleOutsideClick);
        return () => document.removeEventListener('mousedown', handleOutsideClick);
    }, [isMenuOpen]);

    const userInfo = useMemo(() => {
        const userId = currentUserGroup?.userId;
        const details = getUserDetails(formatUserIdToJid(userId));
        let ts = currentStory?.createdAt || currentStory?.created_at || currentStory?.timestamp;
        let timeStr = '';
        if (ts) {
            const d = new Date(parseInt(ts) > 1_000_000_000_000 ? parseInt(ts) : parseInt(ts) * 1000);
            timeStr =
                d.toLocaleDateString([], { month: 'short', day: 'numeric' }) +
                ' at ' +
                d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true });
        }
        return {
            displayHeader: isMyStory ? 'Your Story' : details?.displayName || userId || 'Unknown',
            displayName: isMyStory ? details?.initialName : details?.displayName || userId || 'Unknown',
            avatar: details?.image || '',
            timestamp: timeStr,
        };
    }, [currentUserGroup, currentStory, isMyStory]);

    // ---------- Media fetching ----------
    // sid and gUserId are passed explicitly to avoid stale closure over storyId/currentUserGroup.

    const fetchMedia = useCallback(
        async (token, sid, gUserId) => {
            if (!token) {
                setLoading(false);
                return;
            }
            lastFetchedStoryIdRef.current = sid;
            setLoading(true);
            setCurrentMediaUrl('');

            // Revoke the previous objectURL to free memory before loading the next story
            if (objectUrlRef.current) {
                URL.revokeObjectURL(objectUrlRef.current);
                objectUrlRef.current = null;
            }

            try {
                let mediaUrl = null;

                // ---- IndexedDB cache check ----
                const cachedBlob = await localDb.getStoryMedia(sid);
                if (cachedBlob) {
                    // Cache hit — no network call needed
                    const objectUrl = URL.createObjectURL(cachedBlob);
                    objectUrlRef.current = objectUrl;
                    mediaUrl = objectUrl;
                } else {
                    // Cache miss — get presigned URL from SDK then fetch the actual blob
                    const sdkRes = await SDK.downloadStoryMedia(token);
                    if (!sdkRes || (sdkRes.statusCode !== 200 && sdkRes.status !== 200)) return;

                    const presignedUrl = sdkRes.data?.url || sdkRes.data?.blobUrl;
                    if (!presignedUrl) return;

                    try {
                        const mediaRes = await fetch(presignedUrl);
                        if (mediaRes.ok) {
                            const blob = await mediaRes.blob();
                            await localDb.setStoryMedia(sid, blob);
                            const objectUrl = URL.createObjectURL(blob);
                            objectUrlRef.current = objectUrl;
                            mediaUrl = objectUrl;
                        } else {
                            // fetch failed — fall back to presigned URL directly (not cached)
                            mediaUrl = presignedUrl;
                        }
                    } catch {
                        // Network error — fall back to presigned URL directly
                        mediaUrl = presignedUrl;
                    }
                }

                if (!mediaUrl) return;
                setCurrentMediaUrl(mediaUrl);

                // ---- Seen status / viewers (same for cache hit and miss) ----
                if (!isMyStory) {
                    const sendSeenStatusRes = await SDK.sendStorySeenStatus(sid, gUserId);
                    if (sendSeenStatusRes?.statusCode === 200) {
                        const updatedStories = remoteUserStoriesRef.current.map(user => {
                            if (user.userId !== gUserId) return user;
                            const updatedUserStories = user.stories.map(story => {
                                if (story.storyId !== sid) return story;
                                return { ...story, seen: 1 };
                            });
                            return buildStoryGroup({
                                userId: user.userId,
                                stories: updatedUserStories,
                                includeMeta: true,
                            });
                        });
                        dispatch(setStories(updatedStories));
                    }
                } else {
                    setViewersFetching(true);
                    const viewersData = await SDK.getStoryViewerList(sid);
                    if (viewersData?.statusCode === 200) {
                        dispatch(
                            mergeStoryViewers({
                                userId: gUserId,
                                storyId: sid,
                                viewers: viewersData.data.viewers || [],
                            }),
                        );
                        setViewersPagination({
                            currentPage: viewersData.data.currentPage ?? 0,
                            totalPages: viewersData.data.totalPages ?? 1,
                        });
                    }
                    setViewersFetching(false);
                }
            } catch (error) {
                logger.error('Failed to fetch story media:', error);
                setViewersFetching(false);
            } finally {
                setLoading(false);
            }
        },
        [isMyStory, dispatch, localDb],
    );

    // Revoke objectURL on unmount to prevent memory leaks
    useEffect(() => {
        return () => {
            if (objectUrlRef.current) {
                URL.revokeObjectURL(objectUrlRef.current);
                objectUrlRef.current = null;
            }
        };
    }, []);

    // Re-fetch and reset playback state whenever the story changes.
    // Pass storyId and userId explicitly so fetchMedia never reads stale closure values.
    useEffect(() => {
        setProgress(0);
        pausedProgressRef.current = 0;
        setIsPlaying(true);
        fetchMedia(fileToken, storyId, currentUserGroup?.userId);
        setIsLiked(Boolean(currentStory?.liked));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userIndex, storyIndex]);

    // ---------- IQ-driven remote story deletion ----------
    // Fires whenever storyGroups changes (e.g. removeStoryById dispatched from storyListener).
    // Detects if the story currently on screen was deleted and navigates to the next one.
    useEffect(() => {
        if (isMyStory) return; // own story deletion is handled by handleDelete
        const deletedId = lastFetchedStoryIdRef.current;
        if (!deletedId) return;

        const userGroup = storyGroups[userIndex];
        const stories = userGroup?.stories || [];
        const stillExists = stories.some(s => (s.storyId || s.story_id) === deletedId);
        if (stillExists) return;

        // Story is gone — navigate to the next available story
        if (stories.length > 0) {
            const newIdx = Math.min(storyIndex, stories.length - 1);
            const nextStory = stories[newIdx];
            setStoryIndex(newIdx);
            setProgress(0);
            fetchMedia(
                nextStory?.mediaUrl || nextStory?.media_url,
                nextStory?.storyId || nextStory?.story_id,
                userGroup.userId,
            );
        } else if (userIndex < storyGroups.length) {
            // This user's stories are all gone; userIndex now points to the next user group
            setStoryIndex(0);
            setProgress(0);
            setCurrentMediaUrl('');
        } else {
            handleMediaClose();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [storyGroups]);

    // ---------- Navigation ----------

    const goToNext = useCallback(() => {
        if (storyIndex < currentStories.length - 1) {
            // Next story within the same user
            setStoryIndex(prev => prev + 1);
        } else if (userIndex < storyGroups.length - 1) {
            // All stories of this user done → jump to first story of next user
            setUserIndex(prev => prev + 1);
            setStoryIndex(0);
        } else {
            // Last story of last user → close preview
            handleMediaClose();
        }
    }, [storyIndex, currentStories.length, userIndex, storyGroups.length, handleMediaClose]);

    const goToPrev = useCallback(() => {
        if (storyIndex > 0) {
            // Previous story within the same user
            setStoryIndex(prev => prev - 1);
        } else if (userIndex > 0) {
            // At the first story of this user → go to last story of previous user
            const prevUserStories = storyGroups[userIndex - 1]?.stories || [];
            setUserIndex(prev => prev - 1);
            setStoryIndex(Math.max(0, prevUserStories.length - 1));
        }
        // Already at the very first story of the very first user — do nothing
    }, [storyIndex, userIndex, storyGroups]);

    // ---------- Auto-advance for images — resumes from paused position ----------

    useEffect(() => {
        if (loading || !isPlaying || isVideo) return;
        // Offset startTime so the timer continues from where it was paused
        const alreadyElapsedMs = (pausedProgressRef.current / 100) * IMAGE_STORY_DURATION_MS;
        const startTime = Date.now() - alreadyElapsedMs;
        const interval = setInterval(() => {
            const elapsed = Date.now() - startTime;
            const newProgress = Math.min((elapsed / IMAGE_STORY_DURATION_MS) * 100, 100);
            setProgress(newProgress);
            if (newProgress >= 100) {
                clearInterval(interval);
                goToNext();
            }
        }, 50);
        return () => clearInterval(interval);
    }, [loading, isPlaying, isVideo, goToNext]);

    // ---------- Play/pause toggle — saves image progress before pausing ----------

    const handlePlayPause = useCallback(() => {
        if (isPlaying && !isVideo) {
            pausedProgressRef.current = progress;
        }
        setIsPlaying(p => !p);
    }, [isPlaying, isVideo, progress]);

    // ---------- Keyboard navigation ----------

    useEffect(() => {
        if (!showReplyInput) return;
        const handleOutsideClick = e => {
            if (replyRef.current && !replyRef.current.contains(e.target)) {
                handleCloseReply();
            }
        };
        document.addEventListener('mousedown', handleOutsideClick);
        return () => document.removeEventListener('mousedown', handleOutsideClick);
    }, [showReplyInput]);

    useEffect(() => {
        const handleKeyDown = e => {
            if (showViewers || showReplyInput) return;
            switch (e.key) {
                case 'ArrowRight':
                    goToNext();
                    break;
                case 'ArrowLeft':
                    goToPrev();
                    break;
                case 'Escape':
                    handleMediaClose();
                    break;
                case ' ':
                    e.preventDefault();
                    handlePlayPause();
                    break;
                default:
                    break;
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [showViewers, showReplyInput, goToNext, goToPrev, handleMediaClose, handlePlayPause]);

    // ---------- Video controls ----------

    const handleVideoTimeUpdate = () => {
        if (videoRef.current) {
            const { currentTime, duration } = videoRef.current;
            if (duration) setProgress((currentTime / duration) * 100);
        }
    };

    useEffect(() => {
        if (!videoRef.current) return;
        if (isPlaying) {
            videoRef.current.play().catch(e => logger.error('Video play failed:', e));
            bgVideoRef.current?.play().catch(() => {});
        } else {
            videoRef.current.pause();
            bgVideoRef.current?.pause();
        }
    }, [isPlaying, currentMediaUrl]);

    // ---------- Click on content area to toggle play/pause ----------

    const handleContentClick = useCallback(
        e => {
            if (showReplyInput) return;
            if (e.target.closest('button')) return;
            handlePlayPause();
        },
        [showReplyInput, handlePlayPause],
    );

    // ---------- Delete ----------

    const handleDelete = async () => {
        try {
            setIsMenuOpen(false);
            const dltStoryRes = await SDK.deleteStory(storyId);

            if (dltStoryRes?.statusCode === 200) {
                toast.success(dltStoryRes?.message);

                // Compute navigation info BEFORE dispatching (selector won't update until re-render)
                const remainingForCurrentUser = currentStories.filter(
                    s => (s.story_id || s.storyId || s.msgId) !== storyId,
                );
                const groupsAfterDelete = storyGroups.filter((g, gIdx) =>
                    gIdx === userIndex ? remainingForCurrentUser.length > 0 : true,
                );

                // Single shared action handles the redux update for both myStories and stories
                dispatch(removeStoryById({ storyId, target: isMyStory ? 'myStories' : 'stories' }));

                if (remainingForCurrentUser.length === 0) {
                    if (userIndex < groupsAfterDelete.length) {
                        setStoryIndex(0);
                    } else {
                        handleMediaClose();
                    }
                } else {
                    const newStoryIndex = Math.min(storyIndex, remainingForCurrentUser.length - 1);
                    const nextStory = remainingForCurrentUser[newStoryIndex];
                    const nextToken = nextStory?.mediaUrl || nextStory?.media_url;
                    const nextStoryId = nextStory?.storyId || nextStory?.story_id;
                    setStoryIndex(newStoryIndex);
                    setProgress(0);
                    fetchMedia(nextToken, nextStoryId, currentUserGroup.userId);
                }
            } else if (dltStoryRes?.statusCode === 400) {
                toast.error('Story cannot be deleted. It may have already expired or been deleted.');
            } else {
                toast.error('Failed to delete story. Please try again.');
            }
        } catch (error) {
            logger.error('Failed to delete story:', error);
            toast.error('Failed to delete story. Please try again.');
        }
    };

    // ---------- Viewers ----------
    // Viewers are fetched inside fetchMedia and merged from IQ — both go to redux.
    // Count and list are derived from redux so IQ updates reflect immediately.

    const viewersList = currentStory?.viewers || [];
    const viewersCount = viewersList.length;

    const handleViewersCountClick = () => {
        setIsPlaying(false);
        setShowViewers(true);
    };

    const handleCloseViewers = () => {
        setShowViewers(false);
        setIsPlaying(true);
    };

    const handleLikeToggle = async () => {
        const newLikeState = !isLiked;

        setIsLiked(newLikeState);
        dispatch(updateStoryViewerLike({ storyId, likedBy: vCardData?.data?.fromUser, liked: newLikeState }));

        try {
            await SDK.sendStoryReaction(storyId, currentUserGroup?.userId, Number(newLikeState));
        } catch (error) {
            setIsLiked(isLiked);
            dispatch(updateStoryViewerLike({ storyId, likedBy: vCardData?.data?.fromUser, liked: isLiked }));
            logger.error('handleLikeToggle', error);
        }
    };

    const handleReply = () => {
        pausedProgressRef.current = progress;
        setIsPlaying(false);
        setShowReplyInput(true);
    };

    const getPlainText = html => {
        if (!html) return '';
        const tmp = document.createElement('div');
        tmp.innerHTML = html;
        return (tmp.innerText || tmp.textContent || '').trim();
    };

    const handleReplyMessage = ({ target: { value } }) => setReplyHtml(value);
    const handleReplyEmptyContent = () => setReplyHtml('');

    const handleCloseReply = () => {
        setShowReplyInput(false);
        setReplyHtml('');
        setIsPlaying(true);
    };

    const handleSendReply = async () => {
        const text = getPlainText(replyHtml);
        if (!text) return;
        try {
            const userProfile = vCardData;
            const seenReceipt = privacySettingsData.seenReceipt === 0 ? '0' : '1';
            const toJid = formatUserIdToJid(currentUserGroup?.userId);
            const msgId = uuidv4();
            const {
                storyId = '',
                storyType = '',
                thumbImage = '',
                caption = '',
                backgroundColor = null,
                fontStyle = null,
            } = currentStory;
            const story = {
                storyId,
                storyType,
                storyThumb: thumbImage,
                storyCaption: caption,
                storyBgColor: backgroundColor,
                storyFontStyle: fontStyle,
            };

            await SDK.sendStoryTextMessage({
                toJid,
                msgId,
                messageText: text,
                receipt_status: seenReceipt,
                story,
            });

            const dataObj = {
                jid: toJid,
                msgType: 'story_reply',
                message: text,
                userProfile,
                chatType: 'chat',
                msgId,
                replyTo: '',
                story,
            };
            const conversationChatObj = await getMessageObjSender(dataObj);
            const recentChatObj = getRecentChatMsgObj(dataObj);
            dispatch(MessageAction(conversationChatObj));
            dispatch(
                ChatMessageHistoryDataAction({
                    data: [conversationChatObj],
                    userJid: toJid,
                }),
            );
            dispatch(RecentChatUpdateAction(recentChatObj));
            handleCloseReply();
        } catch (error) {
            logger.error('handleSendReply', error);
        }
    };

    // ---------- Render ----------

    return (
        <div className="story-preview-wrapper premium-ui">
            <div className="story-header-overlay">
                <button className="story-close-btn" onClick={handleMediaClose}>
                    <CloseWhite />
                </button>

                {/* Progress bar — one segment per story of the current user */}
                <div className="story-progress-container">
                    {currentStories.map((_, idx) => (
                        <div key={idx} className="story-progress-segment">
                            <div
                                className="story-progress-fill"
                                style={{
                                    width: idx < storyIndex ? '100%' : idx === storyIndex ? `${progress}%` : '0%',
                                    transition: idx === storyIndex ? 'none' : 'width 0.3s linear',
                                }}
                            />
                        </div>
                    ))}
                </div>

                <div className="story-header-main-row">
                    <div className="story-header-left-group">
                        <div className="story-user-info-group">
                            <div className="story-avatar-container">
                                <ImageComponent
                                    chatType="chat"
                                    userToken={userInfo?.avatar}
                                    imageToken={userInfo?.avatar}
                                    name={userInfo.displayName}
                                    hideClassNameStatus={true}
                                />
                            </div>
                            <div className="story-user-details">
                                <span className="story-user-name">{userInfo.displayHeader}</span>
                                <span className="story-user-time">{userInfo.timestamp}</span>
                            </div>
                        </div>
                    </div>

                    <div className="story-actions-right">
                        <button className="story-action-btn play-pause-btn" onClick={handlePlayPause}>
                            {isPlaying ? <Recordpause /> : <Playbtn3 />}
                        </button>
                        {isMyStory && (
                            <div className="story-menu-wrapper" ref={menuRef}>
                                <button className="story-action-btn" onClick={() => setIsMenuOpen(o => !o)}>
                                    <IconDotsVertical />
                                </button>
                                {isMenuOpen && (
                                    <div className="story-dropdown-menu">
                                        <button className="story-menu-item" onClick={handleDelete}>
                                            <span>Delete Story</span>
                                            <DeleteStoryIcon />
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className="story-content-container" onClick={handleContentClick}>
                {loading && (
                    <div className="story-loading-overlay">
                        <img
                            src={getThumbBase64URL(
                                currentStory?.thumbImage ||
                                    currentStory?.thumbnail_url ||
                                    currentStory?.msgBody?.media?.thumb_image,
                            )}
                            className="story-blur-thumb"
                            alt=""
                        />
                        <Spinner />
                    </div>
                )}

                {currentMediaUrl && (
                    <>
                        {/* Blurred background fill — eliminates black/empty edges for any aspect ratio */}
                        {isVideo ? (
                            <video
                                ref={bgVideoRef}
                                className="story-media-bg"
                                src={currentMediaUrl}
                                muted
                                playsInline
                                loop
                                aria-hidden="true"
                            />
                        ) : (
                            <img className="story-media-bg" src={currentMediaUrl} alt="" aria-hidden="true" />
                        )}

                        {/* Foreground — rendered at original aspect ratio */}
                        {isVideo ? (
                            <video
                                ref={videoRef}
                                src={currentMediaUrl}
                                autoPlay={isPlaying}
                                onTimeUpdate={handleVideoTimeUpdate}
                                onEnded={goToNext}
                                className="story-media-item"
                            />
                        ) : (
                            <img src={currentMediaUrl} alt="" className="story-media-item" />
                        )}
                    </>
                )}

                <button
                    className="story-nav-arrow left"
                    onClick={e => {
                        e.stopPropagation();
                        goToPrev();
                    }}
                    disabled={isAtVeryStart}
                >
                    <ArrowBackward />
                </button>
                <button
                    className="story-nav-arrow right"
                    onClick={e => {
                        e.stopPropagation();
                        goToNext();
                    }}
                    disabled={isAtVeryEnd}
                >
                    <ArrowForward />
                </button>
            </div>

            <div className="story-footer-overlay">
                {currentStory?.caption && (
                    <div className="story-caption-footer">
                        <p className="story-caption-text">{currentStory.caption}</p>
                    </div>
                )}

                {isMyStory ? (
                    <div className="story-viewers-container">
                        <button className="story-viewers-btn" onClick={handleViewersCountClick}>
                            <ViewIcon />
                            {viewersCount > 0 && <p>{viewersCount}</p>}
                        </button>
                    </div>
                ) : (
                    <div className="story-replay-container">
                        {showReplyInput ? (
                            <div className="story-reply" ref={replyRef}>
                                <div className="story-reply-header">
                                    <span className="story-reply-title">{userInfo.displayName} &middot; Status</span>
                                    <button type="button" className="story-reply-close" onClick={handleCloseReply}>
                                        <StoriesReplyClose />
                                    </button>
                                </div>
                                <div className="story-reply-input-row">
                                    <ContentEditable
                                        id="story-reply-typingContainer"
                                        html={replyHtml}
                                        chatType="chat"
                                        placeholder="Send a reply"
                                        handleMessage={handleReplyMessage}
                                        handleEmptyContent={handleReplyEmptyContent}
                                        handleSendTextMsg={handleSendReply}
                                        handleMentionView={() => {}}
                                        onInputListener={() => {}}
                                        setCursorPosition={() => {}}
                                        setSelectedText={() => {}}
                                    />
                                    <button
                                        type="button"
                                        className="story-reply-send-btn"
                                        onClick={handleSendReply}
                                        disabled={!getPlainText(replyHtml)}
                                    >
                                        <SendMessage />
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <>
                                <button type="button" className="story-like-btn" onClick={handleLikeToggle}>
                                    {isLiked ? <StoriesLiked /> : <StoriesLike />}
                                </button>
                                <button type="button" className="story-replay-btn" onClick={handleReply}>
                                    Reply
                                </button>
                            </>
                        )}
                    </div>
                )}
            </div>

            {showViewers && (
                <StoryViewersModal
                    viewersList={viewersList}
                    viewersFetching={viewersFetching}
                    onClose={handleCloseViewers}
                    storyId={storyId}
                    initialPagination={viewersPagination}
                />
            )}
        </div>
    );
};

StoryMediaPreview.propTypes = {
    handleMediaClose: PropTypes.func.isRequired,
    selectedMessageData: PropTypes.object,
};

export default StoryMediaPreview;
